import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  type PaginatedSelectionMode,
  type UsePaginatedSelectionControls,
  type UsePaginatedSelectionProps,
  type UsePaginatedSelectionReturnType,
  type UsePaginatedSelectionState,
} from './usePaginatedSelection.types';

const initialiseToggleMap = ({
  mode,
  ...props
}: UsePaginatedSelectionProps) => {
  if (mode === 'single') {
    return Object.fromEntries(
      props.initialIncludeList?.map((id) => [id, true]) ?? [],
    );
  } else {
    return Object.fromEntries(
      props.initialExcludeList?.map((id) => [id, true]) ?? [],
    );
  }
};

export const getIsAllRowsSelectedState = (
  includedRowsCount: number,
  totalRowCount: number,
): 'some' | 'all' | 'none' => {
  if (includedRowsCount === 0) return 'none';
  if (includedRowsCount === totalRowCount) return 'all';
  return 'some';
};

/**
 * **DO NOT** call `usePaginatedSelection` directly in the table body as this will cause
 * the entire table to re-render on every selection change. Instead, declare `usePaginatedSelection`
 * with `<PaginatedSelectionContextProvider />` in a wrapper component that then wraps the table
 * component. This allows the main table to skip re-renders when selection state changes, ensuring
 * that checkboxes remain snappy with large/complex tables.
 *
 * ## Operational Modes
 * A hook that can be used to manage bulk selections in server-side paginated data tables. It supports
 * two operational modes:
 *
 * - In `single` mode, users can select individual rows across different pages. The hook maintains an
 * `includeList` of selected row IDs that is persisted across pagination changes.
 * - In `bulk` mode, users can select all rows across all pages and then deselect specific rows as needed.
 * By default, all rows are considered selected and the toggle logic gets inverted. The hook maintains an
 * `excludeList` of deselected row IDs.
 *
 * ## Optionally Controlled Mode
 * The hook's `mode` is optionally controllable. If the `mode` prop is provided, the hook operates in a
 * controlled manner - useful if `mode` needs to act as a more global state that is subscribed to by
 * many components. Also wire up the `onModeChange` callback to respond to mode changes triggered by the
 * returned `changeMode` API.
 *
 * ## Checkbox Component Integration
 * Pass the hook's return value to the `DataTablePaginatedSelectionContextProvider` to provide the selection
 * logic to a `DataTable` component. Swiftly integrate this by rendering `<DataTableSelectAllCheckbox />` and
 * `<DataTableRowSelectCheckbox />` components in the table header and rows respectively.
 */
export const usePaginatedSelection = (
  props: UsePaginatedSelectionProps,
): UsePaginatedSelectionReturnType => {
  const {
    mode,
    currentRowIds,
    totalRowCount,
    onModeChange,
    onIncludeListChange,
    onExcludeListChange,
  } = props;

  const lastToggledRowId = useRef<string | null>(null);
  const [toggleMap, setToggleMap] = useState<Record<string, boolean>>(
    initialiseToggleMap(props),
  );
  const [localMode, setLocalMode] = useState<PaginatedSelectionMode>(
    mode ?? 'single',
  );

  // CONTROLS
  const toggleRow = useCallback((rowId: string) => {
    lastToggledRowId.current = rowId;
    setToggleMap((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
  }, []);

  const toggleRange = useCallback(
    (rowId: string) => {
      const startRowId = lastToggledRowId.current;
      const endRowId = rowId;
      const toggleTransitionMatches =
        startRowId !== null && toggleMap[startRowId] === !toggleMap[endRowId];

      if (startRowId === null || !toggleTransitionMatches) {
        toggleRow(rowId);
      } else {
        const startIdx = currentRowIds.indexOf(startRowId);
        const endIdx = currentRowIds.indexOf(endRowId);
        const [fromIdx, toIdx] =
          startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
        if (toIdx - fromIdx < 0 || fromIdx === -1 || toIdx === -1) return;

        const toggledRangeMap = Object.fromEntries(
          currentRowIds
            .slice(fromIdx, toIdx + 1)
            .map((id) => [id, toggleMap[startRowId]]),
        );
        setToggleMap({ ...toggleMap, ...toggledRangeMap });
      }
    },
    [toggleMap, currentRowIds, toggleRow],
  );

  const clearSelection = useCallback(() => {
    setLocalMode('single');
    setToggleMap({});
    onModeChange?.('single');
  }, [onModeChange]);

  const changeMode = useCallback(
    (mode: PaginatedSelectionMode) => {
      setLocalMode(mode);
      setToggleMap({});
      onModeChange?.(mode);
    },
    [onModeChange],
  );

  const controls: UsePaginatedSelectionControls = useMemo(
    () => ({
      toggleRow,
      toggleRange,
      clearSelection,
      changeMode,
    }),
    [toggleRow, toggleRange, clearSelection, changeMode],
  );

  // DATA

  const getIsRowSelected = useCallback(
    (rowId: string): boolean => {
      if (localMode === 'single') {
        return !!toggleMap[rowId];
      } else {
        return !toggleMap[rowId];
      }
    },
    [localMode, toggleMap],
  );

  const getIsAllRowsSelected = useCallback(() => {
    if (localMode === 'single') {
      const includedCount = Object.values(toggleMap).filter(
        (isIncluded) => isIncluded,
      ).length;
      return getIsAllRowsSelectedState(includedCount, totalRowCount);
    } else {
      const excludedCount = Object.values(toggleMap).filter(
        (isExcluded) => isExcluded,
      ).length;
      return getIsAllRowsSelectedState(
        totalRowCount - excludedCount,
        totalRowCount,
      );
    }
  }, [localMode, toggleMap, totalRowCount]);

  const data: UsePaginatedSelectionState = useMemo(() => {
    if (localMode === 'single') {
      const includeList = Object.entries(toggleMap)
        .filter(([_, isIncluded]) => isIncluded)
        .map(([rowId]) => rowId);
      return {
        mode: localMode,
        includeList,
        selectedRowCount: includeList.length,
        getIsAllRowsSelected,
        getIsRowSelected,
      };
    } else {
      const excludeList = Object.entries(toggleMap)
        .filter(([_, isExcluded]) => isExcluded)
        .map(([rowId]) => rowId);
      return {
        mode: localMode,
        excludeList,
        selectedRowCount: totalRowCount - excludeList.length,
        getIsAllRowsSelected,
        getIsRowSelected,
      };
    }
  }, [
    localMode,
    toggleMap,
    totalRowCount,
    getIsAllRowsSelected,
    getIsRowSelected,
  ]);

  // EFFECTS
  useEffect(() => {
    if (data.mode === 'single') {
      onIncludeListChange?.(data.includeList);
    } else {
      onExcludeListChange?.(data.excludeList);
    }
  }, [data, onIncludeListChange, onExcludeListChange]);

  useLayoutEffect(() => {
    if (mode) {
      setLocalMode(mode);
    }
  }, [mode]);

  return useMemo(() => [data, controls] as const, [data, controls]);
};
